<template>
  <v-card>
    <v-container class="tw-flex tw-justify-between">
      <div>
        <!-- Documentation button -->
        <DocumentationDialogue class="tw-inline" path="manager/moderation/list" :key="$i18n.locale"/>

        <span class="sr-title">
          {{ $t("dashboard.reviewsModeration.list.title") }}
        </span>
        <span class="tw-text-sm">
          <small>({{ count }})</small>
        </span>
      </div>
      <div>
        <v-btn small text @click="previous">{{ $t("common.back") }}</v-btn>
        <v-btn small text @click="next">{{ $t("common.next") }}</v-btn>
      </div>
    </v-container>

    <!-- Content of the list card -->
    <v-card-text v-if="hasReviews" class="pa-0 tw-mt-2">
      <v-list subheader>
        <!-- Scrollable list of reviews -->
        <v-list-item-group v-model="selected" style="max-height: calc(100vh - 292px);overflow-y: auto;">
          <div v-for="review in reviews" :id="review.id + review.database" :key="review.id + review.database">
            <v-list-item :value="review.id + review.database" class="tw-border" color="primary">
              <v-list-item-content>
                <!-- Information about the current item -->
                <v-list-item-subtitle class="tw-flex tw-justify-between">
                  <span>
                    <span :title="$t('dashboard.reviewsModeration.list.moderate')" class="mr-1">
                      <span v-if="review.confidence <= 10 && review.moderate === 1">
                        <v-chip color="warning" text-color="black" x-small>
                          {{ $t("dashboard.reviewsModeration.list.rejected") }}
                        </v-chip>
                      </span>
                      <v-chip v-else-if="review.moderate === 1" color="success" x-small>
                        {{ $t("dashboard.reviewsModeration.list.moderated") }}
                      </v-chip>
                      <v-chip v-else color="blue" text-color="white" x-small>
                        {{ $t("dashboard.reviewsModeration.list.not-moderated") }}
                      </v-chip>
                    </span>
                    <v-chip v-if="review.created_at !== review.updated_at" class="mr-1" x-small>
                      <v-icon left x-small>fa-history</v-icon>
                      {{ review.created_at | moment("from") }}
                    </v-chip>
                    <v-chip
                      :title="$t('dashboard.reviewsModeration.list.rating')"
                      text-color="black"
                      x-small
                      class="mr-1"
                    >
                      <v-icon color="secondary" style="margin-bottom: 3px;" x-small left>fa-star</v-icon>
                      {{ review.rating }}/5
                    </v-chip>
                    <v-chip v-if="review.hashtag" x-small>
                      <v-icon color="blue" left x-small>fa-hashtag</v-icon>
                      {{ review.hashtag }}
                    </v-chip>
                    <v-chip v-if="review.with_gift"
                            color="transparent" text-color="blue" outlined
                            :title="$t('manager.moderation.reviews.with_gift').replace(':','')"
                            style="margin-left: 5px; padding-left: 5px;padding-right: 0;"
                            x-small>
                      <v-icon left x-small>fa-gift</v-icon>
                    </v-chip>
                    <v-chip v-if="review.from_order"
                            color="transparent" text-color="blue" outlined
                            :title="$t('manager.moderation.reviews.from_order').replace(':','')"
                            style="padding-left: 5px;padding-right: 0;"
                            x-small>
                      <v-icon left x-small>fa-shopping-cart</v-icon>
                    </v-chip>
                    <v-chip v-if="review.locale"
                            color="transparent" text-color="blue" outlined
                            :title="review.locale"
                            x-small>
                      <span v-if="review.locale==='en'"><img src="/flags/gb.png" /></span>
                      <span v-if="review.locale==='fr'"><img src="/flags/fr.png" /></span>
                      <span v-if="review.locale==='es'"><img src="/flags/es.png" /></span>
                      <span v-if="review.locale==='it'"><img src="/flags/it.png" /></span>
                      <span v-if="review.locale==='de'"><img src="/flags/de.png" /></span>
                    </v-chip>

                    <v-chip v-if="review.like && review.like.counter > 0" class="mr-1" x-small>
                      <v-icon left x-small>fa-thumbs-up</v-icon>
                      {{ $tc("dashboard.reviewsModeration.list.like_counter", review.like.counter) }}
                    </v-chip>
                  </span>
                  <span>
                    <span class="tw-text-blue-600">
                      {{ getName(review) }}
                    </span>
                  </span>
                </v-list-item-subtitle>

                <span v-if="review.product">
                  <span v-html="$t('reviews.list.product.intro.manager')"></span>
                  <b>{{ review.product.commercial_name }}</b>
                </span>
                <span v-if="review.thematic">
                  <span v-html="$t('reviews.list.thematic.intro.manager')"></span>
                  <b>{{ review.thematic.thematic }}</b>
                </span>
                <span v-if="getOrigin(review) !== 'Securating'">
                  <span v-html="$t('origin.source')"></span>
                  <b> {{ getOrigin(review) }} © </b>
                </span>
                <span class="mt-1 mb-2 sr-list-message">
                  {{ review.message }}
                </span>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-list-item-group>
      </v-list>
      <slot name="pagination"></slot>
    </v-card-text>
    <v-card-text v-else>
      <v-container v-if="refreshAllReadyDone">
        <span class="sr-title">
          {{ $t("dashboard.reviewsModeration.list.empty.messageEmptyResult") }}
        </span>
        <br /><br />
        <span class="tw-text-sm">
          {{ $t("dashboard.reviewsModeration.list.empty.requestDateTime") }}:
          <b>{{ this.$store.state.publications.generateRequestInformation.dt }}</b>
        </span>
      </v-container>
      <v-container v-else>
        <span class="tw-text-sm" v-html="$t('dashboard.reviewsModeration.list.empty.refreshAllReadyNotDone')"> </span>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { first } from "lodash"
import { isset } from "@/helpers"

export default {
  name: "List",
  data: () => ({
    selected: null,
  }),
  watch: {
    selected(value) {
      const review = this.reviews.find(r => {
        return r.id + r.database === value
      })
      if (isset(review)) {
        this.select(review)
      }
    },
  },
  computed: {
    afterDateFr() {
      let date = new Date(this.$store.state.publications.generateRequestInformation.afterDate)
      return date.toLocaleDateString()
    },
    reviews() {
      return this.$store.state.publications.reviews
    },
    hasReviews() {
      return this.$store.state.publications.reviews.length > 0
    },
    count() {
      return this.$store.state.publications.reviews.length
    },
    refreshAllReadyDone() {
      if (this.$store.state.publications.generateRequestInformation) {
        return Object.keys(this.$store.state.publications.generateRequestInformation).length
      }
      return 0
    },
  },
  methods: {
    getUserName(publication) {
      const { user_name } = publication.user
      const semicolonPos = user_name.indexOf(":")
      if (semicolonPos > 0) {
        return ""
      } else {
        return `${user_name}`
      }
    },
    getName(publication) {
      const { first_name } = publication.user
      const { last_name } = publication.user
      let name = ""
      if (first_name) name = name + " " + first_name
      if (last_name) name = name + " " + last_name
      return name.trim()
    },
    displayOrigin(publication) {
      return this.$t("origin.source") + this.getOrigin(publication)
    },
    getOrigin(publication) {
      const securating = "Securating"
      const { user_name } = publication.user
      const semicolonPos = user_name.indexOf(":")
      if (semicolonPos > 0) {
        const prefix = user_name.substr(0, semicolonPos)

        switch (prefix) {
          case "anonymous":
            return securating
          default:
            return `${this.$t("origin." + prefix)}`
        }
      } else {
        return securating
      }
    },
    confidenceColor(confidence) {
      if (confidence <= 5) {
        return "red darken-3"
      } else if (confidence <= 10) {
        return "red"
      } else if (confidence <= 20) {
        return "orange darken-3"
      } else if (confidence <= 30) {
        return "orange"
      } else if (confidence <= 40) {
        return "orange lighten-2"
      } else if (confidence <= 50) {
        return "green darken-3"
      } else if (confidence <= 60) {
        return "green darken-2"
      } else if (confidence <= 70) {
        return "green darken-1"
      } else if (confidence <= 80) {
        return "green"
      } else if (confidence <= 90) {
        return "green lighten-2"
      } else if (confidence <= 100) {
        return "green lighten-2"
      } else {
        return "red darken-4"
      }
    },
    select(review) {
      this.$emit("select", review)
    },
    previous() {
      const index = this.reviews.findIndex(r => r.id + r.database === this.selected) - 1
      const review = this.reviews[index]

      if (isset(review)) {
        this.selected = review.id + review.database
      }
    },
    next() {
      let review
      if (this.selected === null) {
        review = first(this.reviews)
      } else {
        const index = this.reviews.findIndex(r => r.id + r.database === this.selected) + 1
        review = this.reviews[index]
      }

      if (isset(review)) {
        this.selected = review.id + review.database
      }
    },
  },
}
</script>
